import axios from '@/plugins/axios';
import appUtils from '@/utils';
import utils from '@/stores/utils';
import store from '@/stores/store';
import { resolvePermissionCheck } from '@/utils/groupPermissions';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const canViewProviderOrders = () => {
    const permissions = store.getters['session/current_user_permissions'];
    if (resolvePermissionCheck(permissions, 'order.view_order')) {
        return true;
    }
    return appUtils.userHasScope(
        'read_providerorders',
        'write_providerorders',
        'validate_providerorders',
    );
};

const DEFAULT_FILTERS = { offset: 0, limit: 20 };

const createState = (options = {}) => ({
    orders: [],
    orders_url: '/api/providerorders/',
    orders_stats: {},
    orders_filters: DEFAULT_FILTERS,
    orders_count: 0,
    orders_total_count: null,
    orders_total_loading: false,
    orders_loaded: false,
    orders_loading: false,
    orders_loading_errors: null,
    orders_cancel_source: null,
    orders_cancel_source_stats: null,
    fetch_stats: true,
    _storageOptions: options || {},
});

const mutations = {
    updateOrders(state, orders) {
        state.orders = orders;
    },
    updateOrdersUrl(state, orders_url) {
        state.orders_url = orders_url;
    },
    updateOrdersFilters(state, orders_filters) {
        state.orders_filters = orders_filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.orders_filters, fieldsToSave);
        }
    },
    updateOrdersLoading(state, orders_loading) {
        state.orders_loading = orders_loading;
    },
    updateOrdersLoaded(state, orders_loaded) {
        state.orders_loaded = orders_loaded;
    },
    updateOrdersLoadingErrors(state, orders_loading_errors) {
        state.orders_loading_errors = orders_loading_errors;
    },
    updateOrdersCount(state, orders_count) {
        state.orders_count = orders_count;
    },
    updateOrdersTotalCount(state, orders_total_count) {
        state.orders_total_count = orders_total_count;
    },
    updateOrdersTotalLoading(state, orders_total_loading) {
        state.orders_total_loading = orders_total_loading;
    },
    updateOrdersStats(state, orders_stats) {
        state.orders_stats = orders_stats;
    },
    updateFetchStats(state, fetch_stats) {
        state.fetch_stats = fetch_stats;
    },
    updateOrdersCancelSource(state, source) {
        state.orders_cancel_source = source;
    },
    updateOrdersCancelSourceStats(state, source) {
        state.orders_cancel_source_stats = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
};

const actions = {

    async fetchOrdersTotal({ commit, state }) {
        if (!canViewProviderOrders()) {
            commit('updateOrdersTotalCount', 0);
            commit('updateOrdersTotalLoading', false);
            return 0;
        }

        commit('updateOrdersTotalLoading', true);
        commit('updateOrdersTotalCount', null);
        try {
            const establishmentId = state.orders_filters.establishment;
            if (!establishmentId) {
                commit('updateOrdersTotalCount', 0);
                commit('updateOrders', []);
                commit('updateOrdersCount', 0);
                return 0;
            }
            const response = await axios.get(state.orders_url, {
                params: {
                    establishment: establishmentId,
                    limit: 0,
                },
            });
            const count = response.data.count ?? 0;
            commit('updateOrdersTotalCount', count);
            if (count === 0) {
                commit('updateOrders', []);
                commit('updateOrdersCount', 0);
                commit('updateOrdersLoaded', true);
            }
            return count;
        } catch (xhr_error) {
            commit('updateOrdersTotalCount', 0);
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateOrdersTotalLoading', false);
        }
    },

    async fetchOrders({ commit, dispatch, state }, params) {
        if (!canViewProviderOrders()) {
            commit('updateOrdersLoading', false);
            commit('updateOrdersLoaded', true);
            return;
        }

        commit('updateOrdersLoading', true);
        commit('updateOrdersLoadingErrors', null);

        if (state.orders_cancel_source) {
            state.orders_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateOrdersCancelSource', cancelSource);
        try {
            const response = await axios.get(state.orders_url, {
                params: state.orders_filters,
                cancelToken: state.orders_cancel_source?.token,
            });
            commit('updateOrdersLoading', false);
            commit('updateOrders', response.data.results);
            commit('updateOrdersCount', response.data.count);
            commit('updateOrdersCancelSource', null);
            commit('updateOrdersLoaded', true);
            dispatch('fetchOrdersStats');

            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateOrdersLoadingErrors', error.details);
            commit('updateOrdersLoading', false);
            commit('updateOrdersCancelSource', null);
            return Promise.reject(error);
        }
    },


    async fetchOrdersStats({ commit, dispatch, state }, params) {
        if (!canViewProviderOrders()) {
            return;
        }
        if (!state.fetch_stats) {
            return;
        }

        // Cancel any pending request
        if (state.orders_cancel_source_stats) {
            state.orders_cancel_source_stats.cancel("New stats fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateOrdersCancelSourceStats', source);

        try {
            const response = await axios.get(state.orders_url + 'stats/', {
                params: state.orders_filters,
                cancelToken: source.token,
            });
            commit('updateOrdersStats', response.data);
            return response.data;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            throw utils.handleError(xhr_error);
        }
    },

    async init({ commit, dispatch, state }, params) {
        await commit('updateOrders', []);
        await commit('updateOrdersTotalCount', null);
        await commit('updateFetchStats', params.fetch_stats === undefined ? true : params.fetch_stats);
        await commit('updateOrdersLoaded', false);
        if (params.url) {
            await commit('updateOrdersUrl', params.url);
        }
        
        // Update storage options if provided
        if (params.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit('updateOrdersFilters', { 
            ...DEFAULT_FILTERS, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params.filters || {})
        });
    },
};


export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});